/** Key document and library queries. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface KeyDocumentSlot {
  slot: string
  label: string
  expectedStage: string
  present: boolean
  documentId: string | null
  webUrl: string | null
  generatedAt: string | null
  /** The project has passed the stage this document was expected by. A nudge, not a block. */
  overdue: boolean
}

export interface LibraryDocument {
  id: string
  title: string
  kind: string
  slot: string | null
  webUrl: string | null
  sizeBytes: number | null
  modifiedDate: string
}

export function useKeyDocuments(projectId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'key', projectId],
    enabled: Boolean(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/key-documents', {
        params: { path: { project_id: projectId as string } },
      })
      return data as unknown as { slots: KeyDocumentSlot[] }
    },
  })
}

export function useGenerateKeyDocument(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (slot: string) => {
      const { data } = await api.POST(
        '/api/v1/projects/{project_id}/key-documents/{slot}/generate',
        { params: { path: { project_id: projectId, slot } } },
      )
      return data as unknown as { id: string; webUrl: string | null; sizeBytes: number }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

export function useDocumentLibrary(projectId?: string) {
  return useQuery({
    queryKey: ['documents', 'library', projectId ?? 'organisation'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/documents', {
        params: { query: projectId ? { project_id: projectId } : {} },
      })
      return data as unknown as { documents: LibraryDocument[] }
    },
  })
}

// --- charter sections -------------------------------------------------------
//
// The text a person writes (or asks for) in the app, which the generated charter then
// carries. The direction matters: a section typed straight into Word is frozen there,
// invisible to every other screen and stale the moment the project moves.

export interface CharterSection {
  id: string | null
  projectId: string
  slot: string
  sectionKey: string
  label: string
  content: string
  origin: 'ai_draft' | 'human'
  /** Written by the model and not yet edited by a person. Cleared by any human save. */
  isAiDraft: boolean
  generatedModel: string | null
  generatedPromptVersion: string | null
  generatedAt: string | null
  modifiedBy: string
  modifiedDate: string
  rowVersion: number
}

export interface CharterSectionsResponse {
  sections: CharterSection[]
  /** False when no API key is configured. The interface hides Generate rather than
   *  offering a button that fails when pressed. */
  draftingAvailable: boolean
  usage: {
    monthToDateTokens: number
    monthlyTokenCap: number
    remainingTokens: number
    promptVersion: string
  }
}

const sectionsKey = (projectId: string | undefined) =>
  ['documents', 'charter-sections', projectId] as const

export function useCharterSections(projectId: string | undefined) {
  return useQuery({
    queryKey: sectionsKey(projectId),
    enabled: Boolean(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/charter-sections', {
        params: { path: { project_id: projectId as string } },
      })
      return data as unknown as CharterSectionsResponse
    },
  })
}

export function useDraftSection(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (sectionKey: string) => {
      const { data } = await api.POST(
        '/api/v1/projects/{project_id}/charter-sections/{section_key}/draft',
        { params: { path: { project_id: projectId, section_key: sectionKey } } },
      )
      return data as unknown as CharterSection
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: sectionsKey(projectId) }),
  })
}

export function useSaveSection(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { sectionKey: string; content: string; rowVersion: number }) => {
      const { data } = await api.PUT('/api/v1/projects/{project_id}/charter-sections/{section_key}', {
        params: { path: { project_id: projectId, section_key: input.sectionKey } },
        // row_version is sent so a second editor is told rather than silently overwritten.
        body: { content: input.content, row_version: input.rowVersion } as never,
      })
      return data as unknown as CharterSection
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: sectionsKey(projectId) }),
  })
}
